import React, { ReactNode, useCallback } from "react";
import { Typography, Tooltip, Flex } from "antd";
import { LinkButton, TxButton } from "ui";
import { getBlockchainExplorerLink, EXPLORER_LINK_TYPE } from "utils";
import { useStores } from "stores";
import { observer } from "mobx-react-lite";

interface ParsedElement {
  type: "text" | "address" | "link";
  value?: string;
  to?: string;
}

const scrollToBottom = () => {
  window.scrollTo({
    top: document.documentElement.scrollHeight,
    behavior: "smooth",
  });
};

const StatementButton = observer(({ message, linkId }: { message: string; linkId: string }) => {
  const { bidCard } = useStores();

  const onClickHandler = useCallback(() => {
    bidCard.setActiveTab("bankStatements");
    setTimeout(() => scrollToBottom(), 300);
  }, [bidCard]);

  return (
    <LinkButton onClick={() => onClickHandler()}>
      <Tooltip title="Реквизиты">
        <Typography.Text strong className="text-underline">
          {message}
        </Typography.Text>
      </Tooltip>
    </LinkButton>
  );
});



export const parseTextMessage = (params: { text: string, tooltipText?: string, network?: 'ETH' | 'TRX' }): ReactNode => {
  // Находим теги <address ...> и <link ...>
  const { text, tooltipText, network } = params;
  const regex = /<address\s+to="([^"]+)"\s*><\/address>|<link\s+to="([^"]+)"\s*>([^<]*)<\/link>/g;

  const elements: ParsedElement[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    const beforeText = text.slice(lastIndex, match.index);
    if (beforeText) {
      elements.push({ type: "text", value: beforeText });
    }

    if (match[1]) {
      // address
      elements.push({ type: "address", to: match[1] });
    } else if (match[2]) {
      // link
      elements.push({ type: "link", to: match[2], value: match[3] });
    }

    lastIndex = regex.lastIndex;
  }

  // Добавляем остаток текста после последнего тега
  const remaining = text.slice(lastIndex);
  if (remaining) elements.push({ type: "text", value: remaining });

  // Преобразуем в JSX
  const result = elements.map((el, index) => {
    switch (el.type) {
      case "text":
        return (
          <Typography.Text key={`text-${index}`} strong>
            {el.value}
          </Typography.Text>
        );

      case "address":
        return (
          <TxButton
            key={`addr-${index}`}
            text={el.to ?? ""}
            href={getBlockchainExplorerLink(network, el.to!, EXPLORER_LINK_TYPE.address)}
            showCopyBtn
            isWalletAddress
          />
        );

      case "link":
        return <StatementButton key={`link-${index}`} message={el.value ?? ""} linkId={el.to ?? ""} />;

      default:
        return null;
    }
  });

  return (
    <Tooltip title={tooltipText}>
      <Flex align="center" gap={4} id="bid-action-notifier" wrap>
        {result}
      </Flex>
    </Tooltip>
  );
};
